// AgencyBoard.js — the read-only staffing board a contracting agency sees.
//
// Public route (/agency/:token), no Supabase session. Everything comes from
// the get-agency-board edge function, which decides the agency's slice under
// the service role — this page filters nothing and can't widen what it gets.
//
// Client names arrive already masked ("Sawyer C. · G054"). That is deliberate
// and is the server's decision, not a display choice made here: an agency
// needs enough to staff a shift, not a child's full identity.
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import AgencyCredentialing from './AgencyCredentialing';
import { portalFetch, isStaffView } from '../lib/agencyPortalClient';
import { offerSummary } from '../lib/agencyOffers';

const fmtH = (n) => `${Number(n || 0).toFixed(n % 1 === 0 ? 0 : 1)}h`;
// Derived from the date rather than a fixed array — the week runs Sunday to
// Saturday (as AxisCare and payroll do), and a hardcoded list is exactly how
// that drifts out of step again.
const dayName = (isoStr) =>
  new Date(`${isoStr}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short' });

const fmtDate = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(`${isoStr}T12:00:00`);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const addDaysIso = (isoStr, n) => {
  const d = new Date(`${isoStr}T12:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const MESSAGES = {
  invalid: 'This link isn’t valid. Ask your contact at A Heart For Care for a new one.',
  expired: 'This link has expired. Ask your contact at A Heart For Care for a new one.',
  revoked: 'This link has been turned off. Ask your contact at A Heart For Care for a new one.',
  error:   'Something went wrong loading the board. Try again in a moment.',
};

const Pill = ({ children, tone = 'neutral', dashed }) => {
  const t = {
    neutral: ['var(--bg2)', 'var(--text2)', 'var(--border)'],
    ours:    ['var(--info-bg)', 'var(--info)', 'var(--info-border)'],
    open:    ['var(--danger-bg)', 'var(--danger)', 'var(--danger-border)'],
    warn:    ['var(--warn-bg)', 'var(--warn)', 'var(--warn-border)'],
  }[tone];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11,
      padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap', margin: '1px 3px 1px 0',
      background: t[0], color: t[1], border: `0.5px ${dashed ? 'dashed' : 'solid'} ${t[2]}`,
    }}>{children}</span>
  );
};

// A child's care plan, when AHFC has shared one. get-agency-care-plan checks
// again that the child is this agency's before it serves the file, stamped
// with the agency's name and the child's, and records the view. The tab is
// opened first so a phone's pop-up blocker lets it through. With none shared
// the child says so, rather than the button silently not being there; staff
// viewing from CAERA get a way to the child's Schedule tab to share one.
function CarePlanButton({ access, child, onOpenClient }) {
  const [busy, setBusy] = useState(false);
  if (!child.carePlan) {
    return (
      <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text3)' }}>
        No care plan shared yet
        {onOpenClient && (
          <button type="button" onClick={() => onOpenClient(child.ref)}
            style={{ marginLeft: 6, fontSize: 11, padding: 0, border: 'none', background: 'none', color: 'var(--accent)', cursor: 'pointer', fontFamily: 'inherit' }}>
            Share one →
          </button>
        )}
      </div>
    );
  }
  const open = async () => {
    const win = window.open('', '_blank');
    setBusy(true);
    try {
      const res = await portalFetch(access, 'get-agency-care-plan', { ref: child.ref });
      if (!res.ok || (res.headers.get('content-type') || '').startsWith('application/json')) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error === 'not_shared'
          ? 'This care plan is no longer shared with you. Ask your contact at A Heart For Care.'
          : (body.error || 'The care plan could not be opened.'));
      }
      const url = URL.createObjectURL(await res.blob());
      if (win) win.location.href = url; else window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      if (win) win.close();
      alert(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" onClick={open} disabled={busy}
      style={{ marginTop: 6, fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 5, cursor: 'pointer', fontFamily: 'inherit',
        background: 'var(--card-bg)', color: 'var(--accent)', border: '0.5px solid var(--accent-border)' }}>
      {busy ? 'Opening…' : '📄 Care plan'}
    </button>
  );
}

// The agency's answer to an offer: "We can cover" or "Can't cover", with the
// name of the person answering and an optional note. Only through the
// agency's own link — AHFC staff viewing from CAERA see the offer, not the
// buttons. The office is told the answer in its Space.
function OfferAnswer({ access, child, onAnswered }) {
  const o = child.offer;
  const [answer, setAnswer] = useState(null);   // 'accept' | 'decline' while the form is open
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (!o) return null;
  const until = o.expiresAt ? new Date(o.expiresAt).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) : null;

  if (o.status === 'accepted') {
    return <div style={{ marginTop: 6 }}><Pill tone="ours">✓ You can cover{o.respondedBy ? ` — ${o.respondedBy}` : ''}</Pill></div>;
  }
  const send = async () => {
    if (!name.trim()) { setErr('Add your name.'); return; }
    setBusy(true); setErr('');
    try {
      const res = await portalFetch(access, 'respond-agency-offer', { offerId: o.id, answer, name, note });
      const data = await res.json().catch(() => ({}));
      if (!data.success) throw new Error(data.error === 'not_found' ? 'This offer is no longer open.' : (data.error || 'Your answer could not be saved.'));
      onAnswered();
    } catch (e) { setErr(e.message); setBusy(false); }
  };
  const input = { width: '100%', fontSize: 12, padding: '5px 7px', borderRadius: 5, border: '0.5px solid var(--border2)', background: 'var(--card-bg)', color: 'var(--text)', fontFamily: 'inherit', marginTop: 5 };
  return (
    <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--text2)' }}>
      <div>{offerSummary({ role_needed: o.roleNeeded, hours_needed: o.hoursNeeded, start_date: o.startDate })}{until ? ` · answer by ${until}` : ''}</div>
      {isStaffView(access) ? null : !answer ? (
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <button type="button" onClick={() => setAnswer('accept')} style={{ fontSize: 11.5, fontWeight: 600, padding: '4px 10px', borderRadius: 5, cursor: 'pointer', fontFamily: 'inherit', background: 'var(--success-bg)', color: 'var(--success)', border: '0.5px solid var(--success)' }}>We can cover</button>
          <button type="button" onClick={() => setAnswer('decline')} style={{ fontSize: 11.5, fontWeight: 600, padding: '4px 10px', borderRadius: 5, cursor: 'pointer', fontFamily: 'inherit', background: 'var(--card-bg)', color: 'var(--text2)', border: '0.5px solid var(--border2)' }}>Can't cover</button>
        </div>
      ) : (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontWeight: 600, color: 'var(--text)' }}>{answer === 'accept' ? 'We can cover' : "Can't cover"}</div>
          <input style={input} value={name} onChange={e => setName(e.target.value)} placeholder="Your name" maxLength={120} />
          <textarea style={{ ...input, minHeight: 46 }} value={note} onChange={e => setNote(e.target.value)} maxLength={500}
            placeholder={answer === 'accept' ? 'Optional: which nurse, from when' : 'Optional: why (e.g. no LPN in that county)'} />
          {err && <div style={{ color: 'var(--danger)', marginTop: 4 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <button type="button" className="btn btn-primary btn-sm" onClick={send} disabled={busy}>{busy ? 'Sending…' : 'Send answer'}</button>
            <button type="button" className="btn btn-outline btn-sm" onClick={() => { setAnswer(null); setErr(''); }} disabled={busy}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

const Tile = ({ label, value, sub, tone }) => (
  <div style={{
    background: tone === 'alert' ? 'var(--danger-bg)' : 'var(--card-bg)',
    border: `0.5px solid ${tone === 'alert' ? 'var(--danger-border)' : 'var(--border)'}`,
    borderRadius: 10, padding: '12px 14px',
  }}>
    <div style={{ fontSize: 9, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.07em', color: tone === 'alert' ? 'var(--danger)' : 'var(--text3)', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 500, color: tone === 'alert' ? 'var(--danger)' : 'var(--text)' }}>{value}</div>
    {sub && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{sub}</div>}
  </div>
);

// The agency's own page: its link is the only authority.
export default function AgencyBoard() {
  const { token } = useParams();
  const access = useMemo(() => ({ token }), [token]);
  return <AgencyPortal access={access} />;
}

/**
 * The portal itself, as the agency sees it. `access` is the agency's link, or
 * — from CAERA's Schedule → Agency portal — an agency id with the staff
 * member's own sign-in (lib/agencyPortalClient.js). Staff see the same
 * screens, read-only, logged under their name. `onOpenClient(ref)` is CAERA's
 * way to a child's profile; the agency's own link never has one.
 */
export function AgencyPortal({ access, embedded = false, onOpenClient = null }) {
  const staffView = isStaffView(access);
  const [board, setBoard] = useState(null);
  const [status, setStatus] = useState('loading');
  const [weekOffset, setWeekOffset] = useState(0);
  const [tab, setTab] = useState('coverage');

  const load = useCallback(async (offset) => {
    setStatus('loading');
    try {
      const res = await portalFetch(access, 'get-agency-board', { weekOffset: offset });
      const data = await res.json();
      if (!data.success) { setStatus(data.error || 'invalid'); return; }
      setBoard(data);
      setStatus('ok');
    } catch {
      setStatus('error');
    }
  }, [access]);

  useEffect(() => { load(weekOffset); }, [load, weekOffset]);

  if (status !== 'ok' && status !== 'loading') {
    return (
      <div style={{ minHeight: embedded ? 240 : '100vh', background: 'var(--bg)', color: 'var(--text)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ maxWidth: 420, textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Staffing board unavailable</div>
          <p style={{ fontSize: 13.5, color: 'var(--text3)', lineHeight: 1.6 }}>
            {staffView && status === 'invalid'
              ? 'Your CAERA sign-in has ended. Sign out and sign in again, then reopen Agency Portal.'
              : (MESSAGES[status] || MESSAGES.error)}
          </p>
        </div>
      </div>
    );
  }

  const isCredentialing = tab === 'credentials';
  const clients = board?.clients || [];
  const needs = clients.filter(c => c.open > 0 || c.offer);
  const totalOpen = clients.reduce((s, c) => s + c.open, 0);
  const ourHours = clients.reduce((s, c) => s + c.oursHours, 0);
  // The roster is every nurse the agency has on file; the tile counts those
  // actually on this week's schedule.
  const roster = board?.roster || [];
  const scheduledNurses = roster.filter(r => r.hours > 0).length;
  const weekDays = board ? Array.from({ length: 7 }, (_, i) => addDaysIso(board.week.start, i)) : [];

  return (
    <div style={{ minHeight: embedded ? 0 : '100vh', background: 'var(--bg)', color: 'var(--text)' }}>
      <div style={{ maxWidth: 1160, margin: '0 auto', padding: embedded ? '4px 0 40px' : '26px 20px 60px' }}>
        {staffView && (
          <div style={{ background: 'var(--info-bg)', border: '0.5px solid var(--info-border)', color: 'var(--text)', borderRadius: 8, padding: '9px 12px', fontSize: 12.5, marginBottom: 14, lineHeight: 1.5 }}>
            You are seeing exactly what {board?.agency?.name || 'this agency'} sees on its portal. It is read-only from here,
            and everything you open is logged under your own name, not as the agency.
          </div>
        )}

        {/* Header */}
        <div style={{ borderBottom: '0.5px solid var(--border)', paddingBottom: 16, marginBottom: 18 }}>
          <div style={{ fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.09em', color: 'var(--text3)' }}>
            A Heart For Care · Staffing Board
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 600, margin: '6px 0 0' }}>{board?.agency?.name || 'Loading…'}</h1>
          <div style={{ fontSize: 12.5, color: 'var(--text3)', marginTop: 6 }}>
            {isCredentialing
              ? 'Submit your nurses and their documents here.'
              : 'Answer our offers here with We can cover or Can\u2019t cover. For anything else, message us in your Google Chat space.'}
            {!isCredentialing && board?.generatedAt && ` · Updated ${new Date(board.generatedAt).toLocaleString()}`}
          </div>
        </div>

        {/* Week controls. Credentialing isn't week-scoped, so the week picker
            steps aside for it rather than sitting there doing nothing. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          {!isCredentialing && <>
            <button className="btn btn-outline btn-sm" onClick={() => setWeekOffset(o => o - 1)}>← Prev</button>
            <span style={{ fontSize: 13, fontWeight: 500, minWidth: 170, textAlign: 'center' }}>
              {board ? `${fmtDate(board.week.start)} – ${fmtDate(board.week.end)}` : '—'}
            </span>
            <button className="btn btn-outline btn-sm" onClick={() => setWeekOffset(o => o + 1)}>Next →</button>
            {weekOffset !== 0 && <button className="btn btn-outline btn-sm" onClick={() => setWeekOffset(0)}>This week</button>}
          </>}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[['coverage', 'Coverage'], ['week', 'Week grid'], ['roster', 'Our nurses'], ['credentials', 'Credentialing']].map(([k, label]) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid', background: tab === k ? 'var(--accent)' : 'transparent', color: tab === k ? '#fff' : 'var(--text3)', borderColor: tab === k ? 'var(--accent)' : 'var(--border)' }}
              >{label}</button>
            ))}
          </div>
        </div>

        {status === 'loading' && (
          <div className="empty-state" style={{ padding: '40px 0' }}>
            <div className="loading-spinner" style={{ margin: '0 auto' }} />
          </div>
        )}

        {status === 'ok' && isCredentialing && (
          <AgencyCredentialing access={access} agencyName={board?.agency?.name || 'your agency'} />
        )}

        {status === 'ok' && !isCredentialing && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 16 }}>
              <Tile label="Open hours" value={fmtH(totalOpen)} tone={totalOpen > 0 ? 'alert' : undefined} sub={`${needs.length} client${needs.length === 1 ? '' : 's'} need cover`} />
              <Tile label="Your hours" value={fmtH(ourHours)} sub="scheduled this week" />
              <Tile label="Your nurses" value={scheduledNurses} sub={`on this week's schedule · ${roster.length} on file`} />
              <Tile label="Clients shown" value={clients.length} sub="you staff or were offered" />
            </div>

            {/* Needs — the reason the board exists, so it leads */}
            {needs.length > 0 && tab === 'coverage' && (
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="card-header"><span style={{ fontSize: 13, fontWeight: 600 }}>Needs cover</span></div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10, padding: 14 }}>
                  {needs.map(c => (
                    <div key={c.ref} style={{ border: '0.5px solid var(--border)', borderLeft: '3px solid var(--danger)', borderRadius: 6, padding: '10px 12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                        <span style={{ fontWeight: 600, fontSize: 13.5 }}>{c.name}</span>
                        <span style={{ fontFamily: 'monospace', fontSize: 10.5, color: 'var(--text3)' }}>{c.ref}</span>
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--text2)', fontFamily: 'monospace', marginTop: 4 }}>
                        {c.open > 0 ? `${fmtH(c.open)} open` : c.offer ? 'Offered to you' : ''}
                        {c.county ? ` · ${c.county} Co.` : ''}
                      </div>
                      {c.offer?.note && <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 5, lineHeight: 1.45 }}>{c.offer.note}</div>}
                      <OfferAnswer access={access} child={c} onAnswered={() => load(weekOffset)} />
                      <div><CarePlanButton access={access} child={c} onOpenClient={onOpenClient} /></div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="card">
              {tab === 'coverage' && (
                <div className="table-wrapper" style={{ overflowX: 'auto' }}>
                  <table style={{ minWidth: 760 }}>
                    <thead><tr>
                      <th style={{ color: 'var(--text3)' }}>Client</th>
                      <th style={{ color: 'var(--text3)' }}>County</th>
                      <th style={{ color: 'var(--text3)', textAlign: 'right' }}>Authorised</th>
                      <th style={{ color: 'var(--text3)', textAlign: 'right' }}>Staffed</th>
                      <th style={{ color: 'var(--text3)', textAlign: 'right' }}>Open</th>
                      <th style={{ color: 'var(--text3)' }}>Covering this week</th>
                    </tr></thead>
                    <tbody>
                      {clients.length === 0 && (
                        <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
                          Nothing shared with you yet.
                        </td></tr>
                      )}
                      {clients.map(c => (
                        <tr key={c.ref}>
                          <td>
                            <div style={{ fontWeight: 500, fontSize: 13 }}>{c.name}</div>
                            <div style={{ fontFamily: 'monospace', fontSize: 10.5, color: 'var(--text3)' }}>{c.ref}</div>
                            <CarePlanButton access={access} child={c} onOpenClient={onOpenClient} />
                          </td>
                          <td style={{ fontSize: 12, color: 'var(--text3)' }}>{c.county || '—'}</td>
                          <td style={{ textAlign: 'right', fontFamily: 'monospace', fontSize: 12 }}>{c.authorised != null ? fmtH(c.authorised) : '—'}</td>
                          <td style={{ textAlign: 'right', fontFamily: 'monospace', fontSize: 12 }}>{fmtH(c.staffed)}</td>
                          <td style={{ textAlign: 'right', fontFamily: 'monospace', fontSize: 12, color: c.open > 0 ? 'var(--danger)' : 'var(--text3)', fontWeight: c.open > 0 ? 600 : 400 }}>
                            {c.open > 0 ? fmtH(c.open) : '—'}
                          </td>
                          <td>
                            {c.covering.length === 0
                              ? <Pill tone="open">Nobody assigned</Pill>
                              : c.covering.map(cg => (
                                <Pill key={cg.name} tone={cg.ours ? 'ours' : 'neutral'}>
                                  {cg.name}
                                  <span style={{ fontSize: 9, opacity: 0.8 }}>{cg.role || ''}</span>
                                  <span style={{ fontFamily: 'monospace', fontSize: 10, opacity: 0.75 }}>{fmtH(cg.hours)}</span>
                                </Pill>
                              ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {tab === 'week' && (
                <div className="table-wrapper" style={{ overflowX: 'auto' }}>
                  <table style={{ minWidth: 940 }}>
                    <thead><tr>
                      <th style={{ color: 'var(--text3)', minWidth: 150 }}>Client</th>
                      {weekDays.map((d, i) => (
                        <th key={d} style={{ color: 'var(--text3)', textAlign: 'center', minWidth: 106 }}>
                          {dayName(d)}
                          <div style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 500 }}>{d.slice(8)}</div>
                        </th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {clients.map(c => (
                        <tr key={c.ref}>
                          <td>
                            <div style={{ fontSize: 12.5, fontWeight: 500 }}>{c.name}</div>
                            <div style={{ fontFamily: 'monospace', fontSize: 10.5, color: c.open > 0 ? 'var(--danger)' : 'var(--text3)' }}>
                              {c.ref}{c.open > 0 ? ` · ${fmtH(c.open)} open` : ''}
                            </div>
                          </td>
                          {weekDays.map(d => {
                            const cell = c.days?.[d] || [];
                            if (!cell.length) return <td key={d} style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 11 }}>—</td>;
                            return (
                              <td key={d} style={{ padding: 4 }}>
                                {cell.map((e, i) => (
                                  <div key={i} style={{
                                    borderRadius: 4, padding: '4px 6px', marginBottom: 3, fontSize: 10.5, lineHeight: 1.35,
                                    background: !e.who ? 'var(--danger-bg)' : e.ours ? 'var(--info-bg)' : 'var(--bg2)',
                                    border: `0.5px ${!e.who ? 'dashed var(--danger-border)' : `solid ${e.ours ? 'var(--info-border)' : 'var(--border)'}`}`,
                                    color: !e.who ? 'var(--danger)' : e.ours ? 'var(--info)' : 'var(--text2)',
                                  }}>
                                    <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.who || 'OPEN'}</div>
                                    <div style={{ fontFamily: 'monospace', opacity: 0.8 }}>{fmtH(e.hours)} · {e.kind === 'pss' ? 'PSS' : e.role || 'RN'}</div>
                                  </div>
                                ))}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {tab === 'roster' && (
                <div className="table-wrapper">
                  <table>
                    <thead><tr>
                      <th style={{ color: 'var(--text3)' }}>Nurse</th>
                      <th style={{ color: 'var(--text3)' }}>Role</th>
                      <th style={{ color: 'var(--text3)', textAlign: 'right' }}>Hours this week</th>
                      <th style={{ color: 'var(--text3)', textAlign: 'right' }}>Clients</th>
                    </tr></thead>
                    <tbody>
                      {(board.roster || []).length === 0 && (
                        <tr><td colSpan={4} style={{ padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
                          None of your nurses are on the schedule this week.
                        </td></tr>
                      )}
                      {(board.roster || []).map(r => (
                        <tr key={r.name}>
                          <td style={{ fontSize: 13, fontWeight: 500 }}>{r.name}</td>
                          <td style={{ fontSize: 12, color: 'var(--text3)' }}>{r.role || '—'}</td>
                          <td style={{ textAlign: 'right', fontFamily: 'monospace', fontSize: 12 }}>{fmtH(r.hours)}</td>
                          <td style={{ textAlign: 'right', fontFamily: 'monospace', fontSize: 12, color: 'var(--text3)' }}>{r.clients}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div style={{ borderTop: '0.5px solid var(--border)', padding: '9px 16px', display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 11, color: 'var(--text3)' }}>
                <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'var(--info-bg)', border: '0.5px solid var(--info-border)', marginRight: 5, verticalAlign: -1 }} />Your nurse</span>
                <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'var(--bg2)', border: '0.5px solid var(--border)', marginRight: 5, verticalAlign: -1 }} />Another provider</span>
                <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'var(--danger-bg)', border: '0.5px dashed var(--danger-border)', marginRight: 5, verticalAlign: -1 }} />Open shift</span>
              </div>
            </div>

            <p style={{ fontSize: 11, color: 'var(--text3)', marginTop: 18, lineHeight: 1.6, maxWidth: '75ch' }}>
              Confidential — protected health information shared under a business associate agreement.
              Client names are shortened on purpose. Don’t forward this link; it identifies your agency
              and every visit is logged. Shifts are scheduled in AxisCare, which stays the record of truth.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
